import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { useMyPageProfileEditor } from '../features/my-page-profile/model/use-my-page-profile-editor';
import { useMyPageProfilePreviewProgress } from '../features/my-page-profile/model/use-my-page-profile-preview-progress';
import { MyPageAvatarEditorOverlay } from '../features/my-page-profile/ui/my-page-avatar-editor-overlay';
import { MyPageProfileEditSheet } from '../features/my-page-profile/ui/my-page-profile-edit-sheet';
import { MyPageProfileHeader } from '../features/my-page-profile/ui/my-page-profile-header';
import { MyPageProfilePreviewCard } from '../features/my-page-profile/ui/my-page-profile-preview-card';
import {
  type EmployeeRating,
  type EmployeeGoal,
  type EmployeeProfile,
  getEmployeeGoal,
  getEmployeeProfile,
  getEmployeeRating,
} from '../shared/api/employee.service';
import { useEmployeeSession } from '../shared/hooks/use-employee-session';
import { resolveEmployeeSessionCountryCode } from '../shared/model';
import { BottomNavPageLayout, HomeDashboardBottomNav } from '../shared/ui';

const FRAME_TOP_PADDING_PX = 20;
const FRAME_SIDE_PADDING_PX = 30;
const FOOTER_TOP_PADDING_PX = 10;
const FOOTER_BOTTOM_PADDING_PX = 13;

const Screen = styled.div.attrs({
  'data-ui': 'my-page-screen',
})`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding-top: ${({ theme }) =>
    `max(${theme.scale.px(FRAME_TOP_PADDING_PX)}, env(safe-area-inset-top))`};
  padding-right: ${({ theme }) =>
    `max(${theme.scale.px(
      FRAME_SIDE_PADDING_PX,
    )}, env(safe-area-inset-right))`};
  padding-left: ${({ theme }) =>
    `max(${theme.scale.px(FRAME_SIDE_PADDING_PX)}, env(safe-area-inset-left))`};
`;

const ContentLayout = styled.div.attrs({
  'data-ui': 'my-page-content',
})`
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-rows: auto auto minmax(0, 1fr);
  row-gap: ${({ theme }) => theme.scale.px(10)};
`;

const EditSheetSlot = styled.div.attrs({
  'data-ui': 'my-page-edit-sheet-slot',
})`
  min-height: 0;
  padding-top: ${({ theme }) => theme.scale.px(6)};
  margin-right: ${({ theme }) => `-${theme.scale.px(FRAME_SIDE_PADDING_PX)}`};
  margin-left: ${({ theme }) => `-${theme.scale.px(FRAME_SIDE_PADDING_PX)}`};
`;

const BottomNavSlot = styled.div.attrs({
  'data-ui': 'my-page-bottom-nav-slot',
})`
  flex-shrink: 0;
  display: grid;
  justify-items: center;
  padding-top: ${({ theme }) => theme.scale.px(FOOTER_TOP_PADDING_PX)};
  padding-right: ${({ theme }) =>
    `max(${theme.scale.px(
      FRAME_SIDE_PADDING_PX,
    )}, env(safe-area-inset-right))`};
  padding-bottom: ${({ theme }) =>
    `max(${theme.scale.px(
      FOOTER_BOTTOM_PADDING_PX,
    )}, env(safe-area-inset-bottom))`};
  padding-left: ${({ theme }) =>
    `max(${theme.scale.px(FRAME_SIDE_PADDING_PX)}, env(safe-area-inset-left))`};
`;

export function MyPage() {
  const { authContact, status } = useEmployeeSession();
  const goalQuery = useQuery({
    enabled: status === 'authenticated',
    queryFn: getEmployeeGoal,
    queryKey: ['employee', 'goal'],
  });
  const profileQuery = useQuery({
    enabled: status === 'authenticated',
    queryFn: getEmployeeProfile,
    queryKey: ['employee', 'profile'],
  });
  const ratingQuery = useQuery({
    enabled: status === 'authenticated',
    queryFn: getEmployeeRating,
    queryKey: ['employee', 'rating'],
  });
  const goal = goalQuery.data ?? null;
  const profile = profileQuery.data ?? null;
  const rating = ratingQuery.data;
  const countryCode = resolveEmployeeSessionCountryCode(profile, authContact);
  const contentKey = [
    profile?.updatedAt ?? 'profile',
    profile?.showRating ?? true,
    goal?.text ?? '',
    goal?.targetAmount ?? 0,
    goal?.currentAmount ?? 0,
  ].join(':');

  return (
    <MyPageContent
      key={contentKey}
      countryCode={countryCode}
      goal={goal}
      profile={profile}
      rating={rating}
    />
  );
}

type MyPageContentProps = {
  countryCode: string;
  goal: EmployeeGoal | null;
  profile: EmployeeProfile | null;
  rating?: EmployeeRating;
};

function MyPageContent({
  countryCode,
  goal,
  profile,
  rating,
}: MyPageContentProps) {
  const navigate = useNavigate();
  const [isAvatarEditorOpen, setIsAvatarEditorOpen] = useState(false);
  const {
    goalAmountValue,
    goalText,
    isRatingVisible,
    profileName,
    ratingValueLabel,
    setIsRatingVisible,
    updateGoalAmountValue,
    updateGoalText,
  } = useMyPageProfileEditor({
    countryCode,
    goal,
    profile,
    rating,
  });
  const { previewProgressLabel, previewProgressPercent } =
    useMyPageProfilePreviewProgress(goalAmountValue, countryCode, goal);

  return (
    <BottomNavPageLayout
      frameDataUi="my-page-frame"
      overlay={
        <MyPageAvatarEditorOverlay
          avatarUrl={profile?.avatarUrl ?? null}
          isOpen={isAvatarEditorOpen}
          onClose={() => {
            setIsAvatarEditorOpen(false);
          }}
        />
      }
      pageDataUi="my-page"
      stickyFooter={
        <BottomNavSlot>
          <HomeDashboardBottomNav
            activeTab="my-page"
            onOpenHome={() => {
              navigate('/home');
            }}
            onOpenMyPage={() => {
              navigate('/my-page');
            }}
          />
        </BottomNavSlot>
      }
    >
      <Screen>
        <ContentLayout>
          <MyPageProfileHeader
            onBack={() => {
              navigate('/home');
            }}
          />
          <MyPageProfilePreviewCard
            avatarUrl={profile?.avatarUrl ?? null}
            profileName={profileName}
            goalText={goalText}
            isRatingVisible={isRatingVisible}
            previewProgressLabel={previewProgressLabel}
            previewProgressPercent={previewProgressPercent}
            ratingValueLabel={ratingValueLabel}
          />
          <EditSheetSlot>
            <MyPageProfileEditSheet
              avatarUrl={profile?.avatarUrl ?? null}
              countryCode={countryCode}
              goalAmountValue={goalAmountValue}
              goalText={goalText}
              isRatingVisible={isRatingVisible}
              onAvatarEditPress={() => {
                setIsAvatarEditorOpen(true);
              }}
              onGoalAmountSave={updateGoalAmountValue}
              onGoalTextSave={updateGoalText}
              onRatingVisibilityChange={setIsRatingVisible}
              ratingValueLabel={ratingValueLabel}
            />
          </EditSheetSlot>
        </ContentLayout>
      </Screen>
    </BottomNavPageLayout>
  );
}
